<script lang="ts" setup>
import { computed, ref, watch, nextTick, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useAllRuns, useGetWorkflowRun, useOrgRunFilterProducts } from "@/queries/runs";
import { useProjects, type Paged } from "@/queries/projects";
import { useRunProgress, applyProgress, applyServiceCounts } from "@/composables/useRunProgress";
import { useRunSimulator } from "@/composables/useRunSimulator";
import { useDebugStore } from "@/stores/debug-store";
import type { Project, RunHistory } from "@/types";
import { isFinishedRunStatus } from "@/types";
import HistoryPanel from "@/views/builder/components/HistoryPanel.vue";
import StackedWorkflowView from "@/views/builder/components/StackedWorkflowView.vue";
import RunCanvas from "@/views/runhistory/RunCanvas.vue";
import RunSummaryHeader from "@/views/builder/components/RunSummaryHeader.vue";
import RunWorkflowModal from "@/views/projects/components/tabs/RunWorkflowModal.vue";
import RunStatsModal from "@/views/builder/components/RunStatsModal.vue";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import { useHistoryManagement } from "@/views/builder/composables/useHistoryManagement";
import { useHistoryFilters } from "@/views/builder/composables/useHistoryFilters";
import { useBuilderPrefsStore } from "@/stores/builder-prefs-store";
import { useBuilderNavigation } from "@/views/builder/composables/useBuilderNavigation";

// Org-wide run history — every project's runs on one page. The per-project History tab is a sibling of
// this and is untouched; this view reuses the same list/detail components with its own data source
// (useAllRuns), its own query-key namespace (`all-runs`), and its own routes (`global-runs-*`). It
// shows a Project column because rows span projects; the project a run needs (Open-in-Builder, Re-run)
// is resolved from the run's own runningProjectId, which can be empty for legacy/orchestrated runs.

const router = useRouter();
const route = useRoute();

// Filters, without persistence — the org-wide page resets its filters per visit (v1). The persisted
// scopes are 'project' | 'workflow'; there is no 'global' slot and adding one is a deliberate v2.
const filters = useHistoryFilters();
const { allRuns, allRunsTotal, allRunsLoading, allRunsFetching } = useAllRuns(filters.serverFilterParams);
const historyMgmt = useHistoryManagement(allRuns);
const builderPrefs = useBuilderPrefsStore();

// Canvas vs stack — the SAME persisted pref the builder and project tab use, so the choice follows
// the user across all three run-history surfaces. Falls back to stack on small screens, canvas otherwise.
const viewMode = computed<"canvas" | "stack">(
  () => builderPrefs.viewMode ?? (window.innerWidth < 768 ? "stack" : "canvas")
);
function toggleViewMode() {
  builderPrefs.setViewMode(viewMode.value === "canvas" ? "stack" : "canvas");
}

// Resolve a run's project to a display name for the list's Project column.
const { projects } = useProjects();
const projectNameById = computed(() => {
  const map = new Map<string, string>();
  for (const p of projects.value) map.set(p.id, p.name ?? "");
  return map;
});

// Product filter — the org's owned + subscribed products, prefixed with an "All Products" option.
const { runFilterProducts } = useOrgRunFilterProducts();
const productOptions = computed(() => [
  { value: "all", label: "All Products" },
  ...runFilterProducts.value.map((p) => ({ value: p.id, label: p.name })),
]);

// The list's sub-header, scoped to the active product filter: "All Runs", or "<Product> Runs" when one is
// selected. The page already carries a "Run History" heading, so this reads as the current view, not a
// repeat of the title. Falls back to "All Runs" if the id no longer resolves (product list still loading).
const listTitle = computed(() => {
  const id = filters.historyProductFilter.value;
  if (!id || id === "all") return "All Runs";
  const product = runFilterProducts.value.find((p) => p.id === id);
  return product ? `${product.name} Runs` : "All Runs";
});

// The selected run's project — the context Open-in-Builder and Re-run act in. Empty when the run has
// no project (legacy/orchestrated), which disables both actions.
const selectedProjectId = computed(() => historyMgmt.selectedRun.value?.runningProjectId ?? "");
const selectedProject = computed<Project | null>(
  () => projects.value.find((p) => p.id === selectedProjectId.value) ?? null
);
const hasProjectContext = computed(() => !!selectedProjectId.value && !!selectedProject.value);

// "Open in Builder" routes through the shared navigation resolver, but keyed to the *run's* project so a
// subscriber lands on their fork. Disabled when the run has no project.
const builderNav = useBuilderNavigation(selectedProjectId);
function openRunInBuilder(run: RunHistory) {
  if (!hasProjectContext.value) return;
  builderNav.openInBuilder(selectedProject.value, {
    id: run.workflowId,
    name: run.workflowName,
    // The run doesn't carry the fork flag; the resolver treats "not customized" as read-only-open,
    // which then gates forking behind a confirm — the safe default for a cross-project entry point.
    isCustomized: false,
  }, "history");
}

const stackedWorkflowView = ref<InstanceType<typeof StackedWorkflowView> | null>(null);
const runCanvas = ref<InstanceType<typeof RunCanvas> | null>(null);

// Overlay expand/collapse-all for the stacked view — same controls the builder and project tab
// float over their lists, driving the stacked view's exposed methods.
function expandAllStacked() {
  stackedWorkflowView.value?.handleExpandAll();
}
function collapseAllStacked() {
  stackedWorkflowView.value?.handleCollapseAll();
}

// --- Route → state sync ----------------------------------------------------

const getWorkflowRun = useGetWorkflowRun();
const routeRunId = computed(() => (route.params.runid as string) || "");
const routeServiceId = computed(() => (route.params.serviceid as string) || "");

function applyRouteSelection() {
  const targetRunId = routeRunId.value;
  if (!targetRunId) {
    if (
      !historyMgmt.selectedRun.value &&
      historyMgmt.filteredRunHistory.value.length > 0
    ) {
      historyMgmt.selectRun(historyMgmt.filteredRunHistory.value[0]);
    }
    return;
  }
  if (historyMgmt.selectedRun.value?.id === targetRunId) return;
  const found = historyMgmt.getRunById(targetRunId);
  if (found) {
    historyMgmt.selectRun(found);
    return;
  }
  void selectRunNotOnPage(targetRunId);
}

// A run reached by link (a child run from a transaction) may not be on the loaded page, so it is read by id.
// The answer is used only if the route still names that run; a failed read leaves the selection as it is.
async function selectRunNotOnPage(runId: string) {
  try {
    const run = await getWorkflowRun(runId);
    if (routeRunId.value === runId) historyMgmt.selectRunFromUrl(run);
  } catch {
    // The previous run stays selected.
  }
}

watch([allRuns, routeRunId], () => applyRouteSelection(), { immediate: true });

// Open the deep-linked service in whichever view is active — the stacked view expands the card;
// the canvas opens the transaction drill-in.
async function applyServiceExpansion() {
  const serviceId = routeServiceId.value;
  if (!serviceId) return;
  // Wait for the active view to render the new run's nodes before opening.
  await nextTick();
  if (viewMode.value === "canvas") {
    runCanvas.value?.openService(serviceId);
  } else {
    (stackedWorkflowView.value as any)?.expandNode?.(serviceId);
  }
}

// viewMode too: toggling canvas ↔ stack while a serviceid is deep-linked re-opens it in the now-active view.
watch(
  [() => historyMgmt.selectedRun.value?.id, routeServiceId, viewMode],
  () => applyServiceExpansion()
);

onMounted(() => {
  applyRouteSelection();
  applyServiceExpansion();
});

// --- State → route sync ----------------------------------------------------

function handleSelectRun(run: RunHistory) {
  historyMgmt.selectRun(run);
  router.push({
    name: "global-runs-run",
    params: { workflowid: run.workflowId, runid: run.id },
  });
}

function handleCardExpansionChanged(nodeId: string, expanded: boolean) {
  const run = historyMgmt.selectedRun.value;
  if (!run) return;
  if (expanded) {
    router.replace({
      name: "global-runs-service",
      params: { workflowid: run.workflowId, runid: run.id, serviceid: nodeId },
    });
  } else if (routeServiceId.value === nodeId) {
    router.replace({
      name: "global-runs-run",
      params: { workflowid: run.workflowId, runid: run.id },
    });
  }
}

// --- Run actions -----------------------------------------------------------

async function copyRunId() {
  const id = historyMgmt.selectedRun.value?.id;
  if (!id) return;
  try {
    await navigator.clipboard.writeText(id);
  } catch {
    // clipboard unavailable; ignore
  }
}

// Debug-only run-animation simulator — feeds fake progress frames through the live-update path so the
// canvas/list edges animate without a real backend run. Gated on session debug mode (?debug=1), so it
// ships to every environment but stays hidden unless explicitly activated.
const debug = useDebugStore();
const simulator = useRunSimulator(historyMgmt.selectedRun);

const statsOpen = ref(false);
const rerunModalOpen = ref(false);
const rerunWorkflowId = ref<string | null>(null);
const rerunWorkflowName = ref<string | undefined>(undefined);
const rerunInputs = ref<Record<string, unknown>>({});

// Re-run starts a fresh run in the *run's own project*; disabled when there is none.
async function rerunSelectedRun() {
  const run = historyMgmt.selectedRun.value;
  if (!run || !hasProjectContext.value) return;
  rerunWorkflowId.value = run.workflowId;
  rerunWorkflowName.value = run.workflowName;
  rerunInputs.value = {};
  rerunModalOpen.value = true;

  const detail = await getWorkflowRun(run.id);
  if (rerunModalOpen.value && rerunWorkflowId.value === run.workflowId)
    rerunInputs.value = detail.inputs ?? {};
}

// --- Live progress ---------------------------------------------------------

const queryClient = useQueryClient();

const runProgress = useRunProgress({
  onProgress: (update) => {
    if (historyMgmt.selectedRun.value?.id === update.runId) {
      const applied = applyProgress(historyMgmt.selectedRun.value, update);
      if (applied) historyMgmt.selectedRun.value = applied;
    }
    applyToRunList(update, applyProgress);
  },
  onServiceCounts: (update) => {
    if (historyMgmt.selectedRun.value?.id === update.runId) {
      const applied = applyServiceCounts(historyMgmt.selectedRun.value, update);
      if (applied) historyMgmt.selectedRun.value = applied;
    }
    applyToRunList(update, applyServiceCounts);
  },
  onFinished: async (finished) => {
    void queryClient.invalidateQueries({ queryKey: ["all-runs"] });
    const fetched = await getWorkflowRun(finished.runId);
    if (fetched && historyMgmt.selectedRun.value?.id === fetched.id)
      historyMgmt.selectedRun.value = fetched;
  },
});

// Patch every cached org-wide page: the key carries the filter string, so we match by the `all-runs`
// prefix rather than reconstructing it (same pattern the per-project tab uses for `project-runs`).
function applyToRunList<TUpdate extends { runId: string }>(
  update: TUpdate,
  apply: (previous: RunHistory | null, update: TUpdate) => RunHistory | null
) {
  queryClient.setQueriesData<Paged<RunHistory>>({ queryKey: ["all-runs"] }, (page) => {
    if (!page) return page;
    const index = page.items.findIndex((run) => run.id === update.runId);
    if (index < 0) return page;
    const applied = apply(page.items[index], update);
    if (!applied) return page;
    const items = [...page.items];
    items[index] = applied;
    return { ...page, items };
  });
}

watch(
  [allRuns, historyMgmt.selectedRun],
  ([runs, selected]) => {
    const visible = selected ? [...runs, selected] : runs;
    void runProgress.watch(
      [...new Set(visible.filter((run) => !isFinishedRunStatus(run.status)).map((run) => run.id))]
    );
  },
  { immediate: true }
);

const hasFilteredRuns = computed(() => historyMgmt.filteredRunHistory.value.length > 0);
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h1 class="h2 mb-0">Run History</h1>
        </div>

        <div v-if="allRunsLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-3">Loading run history...</p>
        </div>

        <template v-else>
          <div class="history-tab-layout">
            <HistoryPanel
              scope="project"
              :title="listTitle"
              :show-project="true"
              :project-name-by-id="projectNameById"
              :show-workflow-filter="false"
              :show-product-filter="true"
              :product-filter="filters.historyProductFilter.value"
              :product-options="productOptions"
              :loading="false"
              :filtered-run-history="historyMgmt.filteredRunHistory.value"
              :selected-run="historyMgmt.selectedRun.value"
              :search-query="filters.historySearchQuery.value"
              :status-filter="filters.historyStatusFilter.value"
              :status-filters="filters.historyStatusFilters.value"
              :time-shortcut="filters.historyTimeShortcut.value"
              :date-from="filters.historyDateFrom.value"
              :date-to="filters.historyDateTo.value"
              :sort-by="filters.historySortBy.value"
              :page-length="filters.historyPageSize.value"
              :total-count="allRunsTotal"
              :page="filters.historyPage.value"
              :fetching="allRunsFetching"
              :advanced-open="builderPrefs.historyFiltersAdvancedOpen"
              :advanced-filters-dirty="filters.advancedFiltersDirty.value"
              :grouped="builderPrefs.groupChildRuns"
              @update-search-query="filters.updateSearchQuery"
              @update-status-filter="filters.updateStatusFilter"
              @toggle-status="filters.toggleStatusFilter"
              @update-time-shortcut="filters.updateTimeShortcut"
              @update-date-range="(from, to) => filters.updateDateRange(from, to)"
              @update-product-filter="filters.updateProductFilter"
              @update-sort-by="filters.updateSortBy"
              @update-page-size="filters.updatePageSize"
              @update-page="filters.updatePage"
              @update-advanced-open="builderPrefs.setHistoryFiltersAdvancedOpen"
              @update-grouped="builderPrefs.setGroupChildRuns"
              @reset="filters.resetHistoryFilters"
              @select-run="handleSelectRun"
            />

            <div class="history-tab-detail flex-fill d-flex flex-column">
              <template v-if="historyMgmt.selectedRun.value">
                <RunSummaryHeader
                  :run="historyMgmt.selectedRun.value"
                  :project-id="selectedProjectId"
                  hide-open-in-builder
                >
                  <template #actions>
                    <div class="dropdown">
                      <button
                        class="bb-btn-secondary-cta btn-sm"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        title="More options"
                      >
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu dropdown-menu-end">
                        <li>
                          <a
                            class="dropdown-item"
                            :class="{ disabled: !hasProjectContext }"
                            href="#"
                            @click.prevent="openRunInBuilder(historyMgmt.selectedRun.value)"
                          >
                            <i class="bi bi-wrench me-2"></i>Open in Builder
                          </a>
                        </li>
                        <li>
                          <a
                            class="dropdown-item"
                            :class="{ disabled: !hasProjectContext }"
                            href="#"
                            @click.prevent="rerunSelectedRun"
                          >
                            <i class="bi bi-arrow-clockwise me-2"></i>Re-run
                          </a>
                        </li>
                        <li><hr class="dropdown-divider" /></li>
                        <li>
                          <a class="dropdown-item" href="#" @click.prevent="statsOpen = true">
                            <i class="bi bi-speedometer2 me-2"></i>Stats for nerds
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item" href="#" @click.prevent="copyRunId">
                            <i class="bi bi-clipboard me-2"></i>Copy Run ID
                          </a>
                        </li>
                        <template v-if="debug.debugToolsVisible">
                          <li><hr class="dropdown-divider" /></li>
                          <li>
                            <h6 class="dropdown-header text-muted small text-uppercase">Debug</h6>
                          </li>
                          <li>
                            <a
                              class="dropdown-item small text-muted"
                              :class="{ disabled: simulator.running.value }"
                              href="#"
                              @click.prevent="simulator.start()"
                            >
                              <i class="bi bi-play-circle me-2"></i>Simulate run
                            </a>
                          </li>
                        </template>
                      </ul>
                    </div>
                  </template>
                </RunSummaryHeader>
                <div class="flex-fill position-relative overflow-hidden">
                  <!-- Canvas view — the same read-only run canvas the builder and project tab show.
                       The view-mode toggle rides at the top of the canvas's own floating control
                       cluster, so it looks identical across the three surfaces. -->
                  <RunCanvas
                    v-if="viewMode === 'canvas'"
                    ref="runCanvas"
                    :nodes="historyMgmt.historyNodes.value"
                    :edges="historyMgmt.historyEdges.value"
                    :run-id="historyMgmt.selectedRun.value.id"
                  >
                    <template #controls-top>
                      <button
                        type="button"
                        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                        title="Switch to List View"
                        @click="toggleViewMode"
                      >
                        <i class="bi bi-list-ul"></i>
                      </button>
                    </template>
                  </RunCanvas>
                  <!-- Stack view — card list with a matching floating control cluster (toggle +
                       expand/collapse), same spot and style as the builder / project tab. -->
                  <template v-else>
                    <div class="run-canvas-controls position-absolute d-flex flex-column gap-1">
                      <button
                        type="button"
                        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                        title="Switch to Canvas View"
                        @click="toggleViewMode"
                      >
                        <i class="bi bi-diagram-3"></i>
                      </button>
                      <span class="border-bottom my-1"></span>
                      <button
                        type="button"
                        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                        title="Expand All Services"
                        @click="expandAllStacked"
                      >
                        <i class="bi bi-chevron-bar-down"></i>
                      </button>
                      <button
                        type="button"
                        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                        title="Collapse All Services"
                        @click="collapseAllStacked"
                      >
                        <i class="bi bi-chevron-bar-up"></i>
                      </button>
                      <button
                        type="button"
                        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
                        :class="{ active: builderPrefs.flattenStack }"
                        :title="builderPrefs.flattenStack ? 'Restore indentation' : 'Flatten to full width'"
                        @click="builderPrefs.setFlattenStack(!builderPrefs.flattenStack)"
                      >
                        <i :class="builderPrefs.flattenStack ? 'bi bi-text-indent-right' : 'bi bi-text-indent-left'"></i>
                      </button>
                    </div>
                    <StackedWorkflowView
                      ref="stackedWorkflowView"
                      :nodes="historyMgmt.historyNodes.value"
                      :edges="historyMgmt.historyEdges.value"
                      canvas-mode="history"
                      :has-overlay-controls="true"
                      :flat="builderPrefs.flattenStack"
                      :run-id="historyMgmt.selectedRun.value.id"
                      :run-workflow-id="historyMgmt.selectedRun.value.workflowId"
                      :effective-status-by-service="historyMgmt.effectiveStatusByService.value"
                      :run-coloring-active="historyMgmt.runColoringActive.value"
                      @card-expansion-changed="handleCardExpansionChanged"
                    />
                  </template>
                </div>
              </template>
              <div
                v-else-if="hasFilteredRuns"
                class="d-flex align-items-center justify-content-center text-muted h-100"
              >
                <div class="text-center py-5">
                  <i class="bi bi-arrow-left display-4"></i>
                  <p class="mt-3">Select a run to view its services</p>
                </div>
              </div>
              <div
                v-else
                class="d-flex align-items-center justify-content-center text-muted h-100"
              >
                <div class="text-center py-5">
                  <i class="bi bi-funnel display-4"></i>
                  <h3 class="mt-3 text-muted">No matching runs</h3>
                  <p class="text-muted">Try widening the time range or clearing other filters.</p>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    @click="filters.resetHistoryFilters"
                  >
                    <i class="bi bi-arrow-counterclockwise me-1"></i>Reset filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <RunWorkflowModal
          v-model:show="rerunModalOpen"
          :project-id="selectedProjectId"
          :workflow-id="rerunWorkflowId"
          :workflow-name="rerunWorkflowName"
          :initial-inputs="rerunInputs"
        />

        <RunStatsModal v-model:show="statsOpen" :run="historyMgmt.selectedRun.value" />
      </PanelCard>
    </PanelGrid>
  </main>
</template>

<style scoped lang="scss">
.history-tab-layout {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 600px;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.375rem;
  overflow: hidden;
  position: relative;
}

.history-tab-detail {
  min-width: 0;
  background: var(--bs-tertiary-bg) url(/images/watermark.png) bottom right no-repeat;
}

// Mobile — pull the floating controls in to match the tightened list spacing (top/right
// mirror the service stack's mobile padding-top and the header panel's edge gap).
@media (max-width: 768px) {
  .history-tab-detail .run-canvas-controls {
    top: 0.375rem;
    right: 0.5rem;
  }
}
</style>
